import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Volume2, Pause, Loader2, Square } from "lucide-react";
import { toast } from "sonner";

let activeAudio = null;

const STATUS_LABEL = { loading: "Preparing audio…", playing: "Pause", paused: "Resume" };

function StatusIcon({ state }) {
  if (state === "loading") return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
  if (state === "playing") return <Pause className="w-3.5 h-3.5" />;
  return <Volume2 className="w-3.5 h-3.5" />;
}

function titleFor(state, label, costHint) {
  if (state === "idle") return `${label} (generates audio once, ${costHint} AI credits)`;
  return state === "playing" ? "Pause" : "Resume";
}

// Read-aloud for chat answers, guides and modules. Audio is generated ONLY on click (Universal Key credits) and cached server-side.
export default function ListenButton({ messageId, src, label = "Listen", costHint = "~1¢", testid = "listen-btn" }) {
  const path = src || `/tts/${messageId}`;
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => () => { audioRef.current?.pause(); if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const ensureAudio = async () => {
    if (audioRef.current) return audioRef.current;
    setState("loading");
    const r = await api.get(path, { responseType: "blob" });
    urlRef.current = URL.createObjectURL(r.data);
    const a = new Audio(urlRef.current);
    a.onended = () => setState("idle");
    a.onpause = () => setState((s) => {
      if (s === "loading") return s;
      return a.ended ? "idle" : "paused";
    });
    a.onplay = () => setState("playing");
    audioRef.current = a;
    return a;
  };

  const toggle = async () => {
    try {
      if (state === "playing") { audioRef.current.pause(); return; }
      const a = await ensureAudio();
      if (activeAudio && activeAudio !== a) activeAudio.pause();
      activeAudio = a;
      await a.play();
    } catch (e) {
      setState("idle");
      toast.error(e?.response?.status === 402 ? "AI credits exhausted — top up your Universal Key balance" : "Could not generate audio");
    }
  };

  const stop = () => { const a = audioRef.current; if (!a) return; a.pause(); a.currentTime = 0; setState("idle"); };

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        data-testid={testid}
        onClick={toggle}
        disabled={state === "loading"}
        aria-label={state === "playing" ? "Pause reading" : label}
        title={titleFor(state, label, costHint)}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border border-[#F5A623] text-[#7C3F00] bg-white hover:bg-[#FDECD0] transition-colors disabled:opacity-60"
      >
        <StatusIcon state={state} />
        {STATUS_LABEL[state] || label}
      </button>
      {(state === "playing" || state === "paused") && (
        <button type="button" data-testid={`${testid}-stop`} onClick={stop} aria-label="Stop" className="p-1 rounded-full text-[#7C3F00] hover:bg-[#FDECD0]"><Square className="w-3 h-3" /></button>
      )}
    </span>
  );
}
